// Phase 10c — operator-only admin surface for invoices.
//
// Read shapes reuse the 10a InvoiceResponseSchema + InvoiceLineResponseSchema
// (for the detail view). This file adds the admin-only list/detail
// composite, the mutation requests (issue / void / regenerate), and
// the list query.
//
// Numbering, lifecycle, and persona-specific behaviour all live in
// FacilityInvoiceService (closeCycle / issue / void / regenerate);
// the schemas here just shape the API surface.
import { z } from 'zod';
import { InvoiceLineResponseSchema } from './invoice-line.schema.js';
import { InvoiceResponseSchema } from './invoice.schema.js';
import { BuyerTypeSchema, InvoiceStatusSchema } from './enums.schema.js';
import { PaymentEventResponseSchema } from './payment-event.schema.js';
const IsoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');
const ReasonSchema = z
    .string()
    .min(10, 'Reason must be at least 10 characters — document why')
    .max(500);
// ===== list / detail =====
export const AdminInvoiceListQuerySchema = z.object({
    status: InvoiceStatusSchema.optional(),
    buyerType: BuyerTypeSchema.optional(),
    facilityId: z.string().uuid().optional(),
    homecareClientId: z.string().uuid().optional(),
    cycleStartDate: IsoDateSchema.optional(),
    issuedFrom: IsoDateSchema.optional(),
    issuedTo: IsoDateSchema.optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
});
export const AdminInvoiceListItemSchema = InvoiceResponseSchema.extend({
    lineCount: z.number().int().min(0),
    facilityName: z.string().nullable(),
    facilityCode: z.string().nullable(),
    homecareClientUserId: z.string().uuid().nullable(),
});
export const AdminInvoiceListResponseSchema = z.object({
    items: z.array(AdminInvoiceListItemSchema),
    totalCount: z.number().int().min(0),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
export const AdminInvoiceDetailResponseSchema = InvoiceResponseSchema.extend({
    lines: z.array(InvoiceLineResponseSchema),
    paymentEvents: z.array(PaymentEventResponseSchema),
    facilityName: z.string().nullable(),
    facilityCode: z.string().nullable(),
    homecareClientUserId: z.string().uuid().nullable(),
    billingContactEmail: z.string().nullable(),
    /** Short-TTL signed download URL for the rendered invoice PDF (2026-09-21).
     *  null until the render-invoice-pdf job has run. Facility invoices only. */
    pdfUrl: z.string().nullable(),
});
// ===== mutations =====
export const AdminInvoiceIssueRequestSchema = z.object({
    // Optional override of the auto-computed due date (defaults to Net-30
    // from issuedAt per 10-4 STANDARD profile). Operator can use this
    // for one-off Net-15 / Net-45 deviations.
    dueDateOverride: IsoDateSchema.optional(),
});
export const AdminInvoiceVoidRequestSchema = z.object({
    reason: ReasonSchema,
});
export const AdminInvoiceRegenerateRequestSchema = z.object({
    reason: ReasonSchema,
});
// Phase 10f-O3 — operator-initiated force-cycle-close for a single
// facility's specified cycle. Supports Workflow I (operator voids an
// ISSUED invoice over a facility dispute + needs the corrected invoice
// re-issued immediately rather than waiting up to 2 weeks for the next
// Tue 06:00 cycle-close tick).
//
// Idempotency: the partial UNIQUE on (facility_id, cycle_start_date)
// WHERE voided_at IS NULL (10c-8) prevents a duplicate live invoice for
// the same cycle.
export const AdminInvoiceForceCycleCloseRequestSchema = z.object({
    facilityId: z.string().uuid(),
    cycleStartDate: IsoDateSchema,
    reason: ReasonSchema,
});
export const AdminInvoiceForceCycleCloseResponseSchema = z.object({
    facilityId: z.string().uuid(),
    cycleStartDate: IsoDateSchema,
    cycleEndDate: IsoDateSchema,
    invoiceId: z.string().uuid().nullable(),
    skipped: z.enum(['NO_SHIFTS', 'ALREADY_HAS_LIVE_INVOICE']).nullable(),
    lineCount: z.number().int().min(0),
    totalCents: z.number().int().min(0),
});
//# sourceMappingURL=invoice-admin.schema.js.map